// =============================================================================
// features/pwa/InstallAppCard.tsx — Banner "instale o app no celular" (doc 24).
//
// Leva o Manager para a tela de início do operador. O roteiro é DIFERENTE por
// plataforma (F27-S10, doc 24 §11) e o texto genérico "copie o link" só ajuda
// quem está no desktop:
//   - iPhone/iPad no Safari  → passo a passo manual (não existe
//     `beforeinstallprompt` no iOS; a instalação é sempre pelo Compartilhar).
//   - Navegador embutido de app (o operador recebe o link no WhatsApp e toca
//     nele) → não há como instalar dali; o caminho é reabrir no navegador do
//     sistema. Caso mais comum de "não consegui instalar".
//   - Android → instala em um toque quando o Chrome dispara
//     `beforeinstallprompt`; senão, menu ⋮ → Instalar aplicativo.
//   - Desktop → copiar o link para abrir no celular.
//
// Depois de confirmar a instalação ("Já instalei"), o banner some para sempre
// neste dispositivo (localStorage). O "×" apenas dispensa na sessão atual.
//
// Não aparece quando:
//   - a flag `pwa.enabled` está off (ou ainda resolvendo);
//   - o app já roda instalado (standalone) — não faz sentido oferecer instalar;
//   - o usuário já confirmou a instalação neste dispositivo (localStorage);
//   - foi dispensado nesta sessão.
// =============================================================================

import * as React from 'react';

import { Button } from '../../components/ui/Button';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

import { currentInstallPlatform, isStandaloneDisplayMode, type InstallPlatform } from './platform';

/** Marca, por dispositivo, que o usuário confirmou a instalação (não some sozinho). */
const CONFIRMED_STORAGE_KEY = 'elemento-pwa-install-confirmed';

/**
 * `beforeinstallprompt` (Chromium) não está no lib.dom padrão. Guardamos o
 * evento para disparar o prompt nativo dentro do clique do usuário — o
 * navegador exige gesto e só aceita um `prompt()` por evento.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function readConfirmed(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(CONFIRMED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function PhoneGlyph(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <rect x="7" y="2" width="10" height="20" rx="2.5" />
      <path d="M11 18h2" />
    </svg>
  );
}

/** Ícone Compartilhar do iOS (quadrado com seta) — o operador procura ESTE desenho. */
function ShareGlyph(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block w-4 h-4 -mt-0.5 align-middle"
      role="img"
      aria-label="ícone Compartilhar"
    >
      <path d="M12 15V3" />
      <path d="M8.5 6.5 12 3l3.5 3.5" />
      <path d="M6 12H5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-1" />
    </svg>
  );
}

/** Passos numerados — a instalação no celular é uma sequência, não um parágrafo. */
function Steps({ items }: { items: React.ReactNode[] }): React.JSX.Element {
  return (
    <ol className="mt-2 flex flex-col gap-1.5">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2">
          <span
            className="shrink-0 mt-px inline-flex items-center justify-center w-4 h-4 rounded-full font-mono"
            style={{ fontSize: '10px', background: 'var(--info-bg)', color: 'var(--brand-azul)' }}
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <span className="font-sans text-ink-3" style={{ fontSize: 'var(--text-xs)' }}>
            {item}
          </span>
        </li>
      ))}
    </ol>
  );
}

interface InstallCopy {
  title: string;
  intro: string;
  steps: React.ReactNode[] | null;
  /** Mostra o link + botão "Copiar link" — só onde levar o endereço adiante ajuda. */
  showLink: boolean;
}

function copyFor(platform: InstallPlatform, canPromptInstall: boolean): InstallCopy {
  switch (platform) {
    case 'ios-safari':
      return {
        title: 'Instale o Manager na tela de início',
        intro: 'No iPhone e no iPad a instalação é feita pelo menu do Safari:',
        steps: [
          <>
            Toque em <ShareGlyph /> <strong className="text-ink">Compartilhar</strong>, na barra de
            baixo do Safari.
          </>,
          <>
            Role a lista e toque em <strong className="text-ink">Adicionar à Tela de Início</strong>
            .
          </>,
          <>
            Confirme em <strong className="text-ink">Adicionar</strong> — o ícone do Manager aparece
            junto dos outros apps.
          </>,
        ],
        showLink: false,
      };
    case 'ios-in-app':
      return {
        title: 'Abra no Safari para instalar',
        intro:
          'Este navegador não permite adicionar o app à tela de início. Abra o Manager no Safari e a opção aparece:',
        steps: [
          <>
            Toque em <strong className="text-ink">···</strong> (ou em Compartilhar) e escolha{' '}
            <strong className="text-ink">Abrir no Safari</strong>.
          </>,
          <>
            Já no Safari, toque em <ShareGlyph /> <strong className="text-ink">Compartilhar</strong>{' '}
            → <strong className="text-ink">Adicionar à Tela de Início</strong>.
          </>,
        ],
        showLink: true,
      };
    case 'android-in-app':
      return {
        title: 'Abra no Chrome para instalar',
        intro:
          'Este navegador não permite instalar o app. Abra o Manager no Chrome e a opção aparece:',
        steps: [
          <>
            Toque em <strong className="text-ink">⋮</strong> e escolha{' '}
            <strong className="text-ink">Abrir no navegador</strong>.
          </>,
          <>
            Já no Chrome, toque em <strong className="text-ink">⋮</strong> →{' '}
            <strong className="text-ink">Instalar aplicativo</strong>.
          </>,
        ],
        showLink: true,
      };
    case 'android':
      return canPromptInstall
        ? {
            title: 'Instale o Manager no seu celular',
            intro: 'Um toque em "Instalar" e o app fica junto dos outros, sem barra de navegador.',
            steps: null,
            showLink: false,
          }
        : {
            title: 'Instale o Manager na tela de início',
            intro: 'Pelo menu do Chrome:',
            steps: [
              <>
                Toque em <strong className="text-ink">⋮</strong>, no canto superior direito.
              </>,
              <>
                Escolha <strong className="text-ink">Instalar aplicativo</strong> (ou "Adicionar à
                tela inicial") e confirme.
              </>,
            ],
            showLink: false,
          };
    case 'desktop':
    default:
      return {
        title: 'Instale o Manager no seu celular',
        intro:
          'Copie o link, abra no navegador do celular (Safari no iPhone, Chrome no Android) e adicione o app à tela de início.',
        steps: null,
        showLink: true,
      };
  }
}

export function InstallAppCard(): React.JSX.Element | null {
  const { enabled, isLoading } = useFeatureFlag('pwa.enabled');
  const [confirmed, setConfirmed] = React.useState<boolean>(readConfirmed);
  const [sessionDismissed, setSessionDismissed] = React.useState(false);
  const [standalone] = React.useState<boolean>(() => isStandaloneDisplayMode());
  const [platform] = React.useState<InstallPlatform>(() => currentInstallPlatform());
  const [copied, setCopied] = React.useState(false);
  const [installEvent, setInstallEvent] = React.useState<BeforeInstallPromptEvent | null>(null);

  const confirmInstalled = React.useCallback((): void => {
    try {
      window.localStorage.setItem(CONFIRMED_STORAGE_KEY, '1');
    } catch {
      // localStorage indisponível — ao menos esconde na sessão atual.
    }
    setConfirmed(true);
  }, []);

  // `beforeinstallprompt` chega uma única vez e cedo; guardamos o evento para
  // disparar o prompt nativo no clique (o navegador exige gesto do usuário).
  // `preventDefault` impede o mini-infobar do Chrome, que competiria com o card.
  React.useEffect(() => {
    const onBeforeInstall = (event: Event): void => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      setInstallEvent(null);
      confirmInstalled();
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [confirmInstalled]);

  // Reset do "copiado" após 2s — limpo no unmount.
  React.useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (isLoading || !enabled) return null;
  if (standalone) return null;
  if (confirmed || sessionDismissed) return null;

  const appUrl = window.location.origin;
  const copy = copyFor(platform, installEvent !== null);

  const handleCopy = (): void => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(appUrl);
        setCopied(true);
      } catch {
        // clipboard indisponível (contexto não-seguro / permissão) — o link fica
        // visível no card para seleção manual.
        setCopied(false);
      }
    })();
  };

  const handleNativeInstall = (): void => {
    const event = installEvent;
    if (!event) return;
    void (async () => {
      try {
        await event.prompt();
        const choice = await event.userChoice;
        // O evento é de uso único: aceito ou recusado, ele não serve de novo.
        setInstallEvent(null);
        if (choice.outcome === 'accepted') confirmInstalled();
      } catch {
        setInstallEvent(null);
      }
    })();
  };

  return (
    <div
      role="region"
      aria-label="Instalar o app no celular"
      className="mb-5 flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-start sm:justify-between"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
    >
      <div className="flex items-start gap-3 min-w-0">
        <span
          className="shrink-0 flex items-center justify-center w-9 h-9 rounded-md"
          style={{ background: 'var(--info-bg)', color: 'var(--brand-azul)' }}
          aria-hidden="true"
        >
          <PhoneGlyph />
        </span>
        <div className="min-w-0">
          <p className="font-sans font-semibold text-ink" style={{ fontSize: 'var(--text-sm)' }}>
            {copy.title}
          </p>
          <p className="font-sans text-ink-3 mt-0.5" style={{ fontSize: 'var(--text-xs)' }}>
            {copy.intro}
          </p>
          {copy.steps && <Steps items={copy.steps} />}
          {copy.showLink && (
            <p
              className="font-mono truncate mt-2"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--text-2)' }}
              title={appUrl}
            >
              {appUrl}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {installEvent !== null && (
          <Button type="button" variant="primary" size="sm" onClick={handleNativeInstall}>
            Instalar
          </Button>
        )}
        {copy.showLink && (
          <Button
            type="button"
            variant={installEvent === null ? 'primary' : 'ghost'}
            size="sm"
            onClick={handleCopy}
          >
            {copied ? 'Copiado!' : 'Copiar link'}
          </Button>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={confirmInstalled}>
          Já instalei
        </Button>
        <button
          type="button"
          onClick={() => setSessionDismissed(true)}
          aria-label="Dispensar por agora"
          title="Dispensar por agora"
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-sm text-ink-3 transition-colors duration-fast ease hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="w-4 h-4"
            aria-hidden="true"
          >
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
